'use client';

import API from '@/API';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, MoveRight } from 'lucide-react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Icons } from '@/components/ui/icons';

import Card from '@/components/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';
const acceptInsuranceFormSchema = z.object({
  iam: z.string().min(1, 'I am is required'),
  company_name: z.string().min(1, 'Company Name is required'),
  name: z.string().min(1, 'Name is required'),
  monthly_collections: z.string().min(1, 'Monthly Collections is required'),
  email: z.string().email('Invalid email').min(1, 'Email is required'),
  phone: z.string().min(1, 'Phone is required'),
  message: z.string().min(1, 'Message is required'),
});

export const Contact = () => {
  const [loading, setLoading] = React.useState(false);
  const form = useForm<z.infer<typeof acceptInsuranceFormSchema>>({
    resolver: zodResolver(acceptInsuranceFormSchema),
    defaultValues: {
      iam: '',
      company_name: '',
      name: '',
      monthly_collections: '',
      email: '',
      phone: '',
      message: '',
    },
  });

  const onSubmit = (data: any) => {
    setLoading(true);
    API.contact
      .insert(data)
      .then((res) => {
        if (res) {
          toast.success(
            'Thank you for contacting us. One of our sales representatives will reach out to you shortly.'
          );
          setTimeout(() => {
            location.reload();
          }, 3000);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className='w-full py-20' id='contact'>
      <div className='container mx-auto max-w-6xl'>
        <div className='grid gap-10 lg:grid-cols-2'>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-4'>
              <div className='float-start flex w-full'>
                <Badge>Contact</Badge>
              </div>
              <div className='flex flex-col gap-2'>
                <h4 className='font-regular max-w-xl text-left text-3xl tracking-tighter md:text-5xl'>
                  Get a Free Consultation
                </h4>
                <p className='max-w-sm text-left text-lg leading-relaxed tracking-tight text-muted-foreground'>
                  Let HS Med Billing Manage Your Revenue Cycle And Reclaim Your
                  Time & Lost Profit
                </p>
              </div>
            </div>
            <div className='flex flex-row items-start gap-6 text-left'>
              <Check className='mt-2 h-4 w-4 text-primary' />
              <div className='flex flex-col gap-1'>
                <p>Easy to use</p>
                <p className='text-sm text-muted-foreground'>
                  We&apos;ve made it easy to use and understand.
                </p>
              </div>
            </div>
            <div className='flex flex-row items-start gap-6 text-left'>
              <Check className='mt-2 h-4 w-4 text-primary' />
              <div className='flex flex-col gap-1'>
                <p>Fast and reliable</p>
                <p className='text-sm text-muted-foreground'>
                  We&apos;ve made it easy to use and understand.
                </p>
              </div>
            </div>
            <div className='flex flex-row items-start gap-6 text-left'>
              <Check className='mt-2 h-4 w-4 text-primary' />
              <div className='flex flex-col gap-1'>
                <p>Beautiful and modern</p>
                <p className='text-sm text-muted-foreground'>
                  We&apos;ve made it easy to use and understand.
                </p>
              </div>
            </div>
          </div>

          <div className='flex items-center justify-center'>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='w-full space-y-4 p-4'
              >
                <div>
                  <Card className='h-max w-full overflow-auto bg-secondary p-4'>
                    <div className='grid w-full grid-cols-1 gap-4 p-4 sm:grid-cols-2'>
                      <FormField
                        control={form.control}
                        name='iam'
                        render={({ field }) => (
                          <FormItem className='flex flex-col items-start'>
                            <FormLabel>I am a*</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value);
                              }}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <div className='flex w-full items-center justify-between'>
                                    <SelectValue placeholder='I am a' />
                                  </div>
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value='Physician'>
                                  Physician
                                </SelectItem>
                                <SelectItem value='Healthcare Provider'>
                                  Healthcare Provider
                                </SelectItem>
                                <SelectItem value='Practice Staff'>
                                  Practice Staff
                                </SelectItem>
                                <SelectItem value='Labortory'>
                                  Labortory
                                </SelectItem>
                                <SelectItem value='Consultant'>
                                  Consultant
                                </SelectItem>
                                <SelectItem value='Hospital'>
                                  Hospital
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='company_name'
                        render={({ field }) => (
                          <FormItem className='flex flex-col items-start'>
                            <FormLabel>Practice or Company Name*</FormLabel>
                            <FormControl>
                              <Input
                                placeholder='Enter Practice or Company Name'
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='name'
                        render={({ field }) => (
                          <FormItem className='flex flex-col items-start'>
                            <FormLabel>Name*</FormLabel>
                            <FormControl>
                              <Input placeholder='Enter Name' {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='monthly_collections'
                        render={({ field }) => (
                          <FormItem className='flex flex-col items-start'>
                            <FormLabel>Monthly Collections*</FormLabel>
                            <FormControl>
                              <Input
                                placeholder='Enter Monthly Collections'
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='email'
                        render={({ field }) => (
                          <FormItem className='flex flex-col items-start'>
                            <FormLabel>Email*</FormLabel>
                            <FormControl>
                              <Input placeholder='Enter Email' {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='phone'
                        render={({ field }) => (
                          <FormItem className='flex flex-col items-start'>
                            <FormLabel>Phone*</FormLabel>
                            <FormControl>
                              <Input placeholder='Enter Phone' {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='message'
                        render={({ field }) => (
                          <FormItem className='col-span-1 flex flex-col items-start sm:col-span-2'>
                            <FormLabel>Message*</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder='Enter Message'
                                {...field}
                                rows={6}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className='flex w-full justify-end gap-4 p-4'>
                      <Button
                        type='submit'
                        className='w-full items-center gap-4'
                      >
                        {loading && (
                          <Icons.spinner className='mr-2 h-4 w-4 animate-spin' />
                        )}
                        Get a Free Consultation
                        <MoveRight className='h-4 w-4' />
                      </Button>
                    </div>
                  </Card>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};
